/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { textStyleClass } from '@sirius/typography';
import { BehaviorSubject } from 'rxjs';
import { IFormSelectOption } from '../_shared/models/form/form-select.model';
import { FormValidationCheckerService } from '../_shared/services/form-validation-checker.service';
import { IconComponent } from '../icon/icon.component';
import { SearchComponent } from '../search/search.component';

@Component({
    selector: 'sirius-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'sir-text-style-paragraph-m-default' },
    imports: [IconComponent, SearchComponent, CommonModule]
})
export class SelectComponent implements ControlValueAccessor, OnInit {
    private _ngControl = inject(NgControl, { self: true });
    validationService = inject(FormValidationCheckerService);

    @Input() placeholder = '';
    @Input() label = '';
    @Input() id = '';
    @Input() name = '';
    @Input() options: IFormSelectOption[] = [];
    @Input() identifier = '';

    focused = false;
    optionTexts: string[] = [];
    selectedOption?: IFormSelectOption;

    protected _filteredOptions = new BehaviorSubject<IFormSelectOption[]>([]);
    filteredOptions$ = this._filteredOptions.asObservable();

    private _opened = new BehaviorSubject<boolean>(false);
    opened$ = this._opened.asObservable();

    private _valid = new BehaviorSubject<boolean>(true);
    valid$ = this._valid.asObservable();

    get control() {
        return this._ngControl.control as FormControl;
    }

    protected get labelSize(): 's' | 'l' {
        return this.control?.value ? 's' : 'l';
    }

    /** Typography utility classes for the select label (ADR 0001). */
    protected get labelClass(): string {
        return `sir-select-label ${textStyleClass('system', this.labelSize)} sir-text-style-color-secondary`;
    }

    onChange = (value: string) => {};
    onTouched = () => {};

    constructor() {
        const _ngControl = this._ngControl;

        _ngControl.valueAccessor = this;
    }

    ngOnInit(): void {
        this.control.statusChanges.subscribe((status) => {
            this._valid.next(status !== 'INVALID' ? true : false);
        });

        this.setInitValue();
        this.optionTexts = this.options.map((x: IFormSelectOption) => x.text) ?? [];

        this.opened$.subscribe((x: boolean) => {
            if (!x) this._filteredOptions.next([...this.options]);
        });
    }

    onClick(event: Event) {
        event.stopPropagation();
        this._opened.next(!this._opened.getValue());
    }

    onEscape(): void {
        this._opened.next(false);
    }

    onFocus() {
        if (!this.focused) {
            this.onTouched();
        }

        this.focused = true;
    }

    onBlur(event: FocusEvent, containerEl: HTMLElement) {
        const relatedTarget = event.relatedTarget as HTMLElement;

        if (!containerEl.contains(relatedTarget)) {
            this.focused = false;
            this._opened.next(false);
        }
    }

    filterOptions(matches: string[]) {
        const filtered = this.options.filter((x: IFormSelectOption) => matches.includes(x.text));

        this._filteredOptions.next(filtered);
    }

    setInitValue(): void {
        const selected = this.options.findIndex((x) => x.selected);
        if (selected > -1) {
            this.setValue(this.options[selected]);
        }
    }

    setValue(opt: IFormSelectOption): void {
        this.setSelectedOption(opt);
        this.onChange(opt.value);
    }

    setSelectedOption(opt: IFormSelectOption) {
        if (this.selectedOption) {
            this.selectedOption.selected = false;
        }

        opt.selected = true;
        this.selectedOption = opt;
    }

    writeValue(value: unknown): void {
        const newOption = this.options.find((x) => {
            return x.value === value;
        });

        if (newOption) {
            this.setSelectedOption(newOption);
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) this.control?.disable();
    }
}
